import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';

// Icône de l'app : caddy cyan avec étoile (pas le logo du magasin !)
const APP_ICON_URL = '/logo-192.png';

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for successful install
    const handleAppInstalled = async () => {
      console.log('PWA installed!');
      setDeferredPrompt(null);
      // Track installation
      try {
        await api.trackAppInstall({
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height
        });
        console.log('Installation tracked!');
      } catch (error) {
        console.error('Failed to track install:', error);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else if (isIOS) {
      setShowInstructions(true);
      // Track iOS install attempt (manuel)
      try {
        await api.trackAppInstall({
          userAgent: navigator.userAgent,
          platform: 'iOS (manual)',
          language: navigator.language,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height
        });
      } catch (error) {
        console.error('Failed to track iOS install:', error);
      }
    } else {
      setShowInstructions(true);
    }
  };

  // Don't show if already installed
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return null;
  }

  return (
    <>
      {/* Install Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed bottom-6 right-6 z-40"
      >
        <Button
          onClick={handleInstallClick}
          className="rounded-full px-6 py-6 shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)',
            boxShadow: '0 0 30px rgba(0, 255, 255, 0.5)'
          }}
          data-testid="install-pwa-button"
        >
          <Download className="w-5 h-5 mr-2" />
          Installer l&apos;app
        </Button>
      </motion.div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-[#0a0015] to-[#1a0033] rounded-3xl p-8 max-w-lg w-full border-2 border-cyan-500 relative"
            style={{ boxShadow: '0 0 50px rgba(0, 255, 255, 0.3)' }}
          >
            <button
              onClick={() => setShowInstructions(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              {/* Icône App - Caddy cyan avec étoile */}
              <div className="mb-6">
                <img 
                  src={APP_ICON_URL} 
                  alt="LPE App" 
                  className="w-24 h-24 mx-auto"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.5))' }}
                />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">
                Installer l&apos;App
              </h2>
              <p className="text-cyan-400 mb-6">La Petite Épicerie</p>

              {isIOS ? (
                <div className="text-left space-y-4">
                  <p className="text-gray-300 font-semibold">Sur iPhone/iPad :</p>
                  <ol className="list-decimal list-inside space-y-3 text-gray-300">
                    <li>Appuyez sur le bouton <strong className="text-cyan-400">Partager</strong> <span className="text-lg">⬆️</span></li>
                    <li>Faites défiler et sélectionnez <strong className="text-cyan-400">&quot;Sur l&apos;écran d&apos;accueil&quot;</strong></li>
                    <li>Appuyez sur <strong className="text-cyan-400">&quot;Ajouter&quot;</strong></li>
                  </ol>
                  <div className="mt-6 p-4 bg-gradient-to-r from-pink-500/20 to-cyan-500/20 border border-cyan-500 rounded-xl">
                    <p className="text-cyan-300 text-sm">
                      ✨ L&apos;icône <strong>LPE</strong> apparaîtra sur votre écran d&apos;accueil !
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-left space-y-4">
                  <p className="text-gray-300 font-semibold">Sur Android :</p>
                  <ol className="list-decimal list-inside space-y-3 text-gray-300">
                    <li>Appuyez sur le menu <strong className="text-cyan-400">⋮</strong> (3 points en haut)</li>
                    <li>Sélectionnez <strong className="text-cyan-400">&quot;Installer l&apos;application&quot;</strong></li>
                    <li>Confirmez en appuyant sur <strong className="text-cyan-400">&quot;Installer&quot;</strong></li>
                  </ol>
                  <div className="mt-6 p-4 bg-gradient-to-r from-pink-500/20 to-cyan-500/20 border border-cyan-500 rounded-xl">
                    <p className="text-cyan-300 text-sm">
                      ✨ L&apos;application s&apos;ouvrira en plein écran comme une vraie app !
                    </p>
                  </div>
                </div>
              )}

              <Button
                onClick={() => setShowInstructions(false)}
                className="w-full mt-6 font-bold py-6"
                style={{
                  background: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)'
                }}
              >
                J&apos;ai compris
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
